/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion, Variants } from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Register GSAP plugins
if (typeof window !== "undefined") {
	gsap.registerPlugin(ScrollTrigger);
}

// ================= TYPES =================
interface SectionItem {
	type: string;
	heading?: string;
	content: string;
}

interface MessageSections {
	title: string;
	subtitle: string;
	heroQuote: string;
	sections: SectionItem[];
}

interface Signatory {
	name: string;
	role: string;
}

interface MessageLabels {
	leadershipInsight: string;
	foundersVision: string;
	deskLabel: string;
}

export interface FoundersMessageData {
	title: string;
	image: string;
	description: string;
	meta: string[];
	signatory: Signatory;
	messageSections: MessageSections;
	labels?: MessageLabels;
}

interface FoundersMessageProps {
	data: FoundersMessageData;
	className?: string;
}

// ================= PRIORITY & POSITION MAPPING =================
const getPriority = (type: string): "high" | "medium" | "low" => {
	const highPriority = ["origin", "philosophy", "closing"];
	const mediumPriority = ["innovation", "ethics"];

	if (highPriority.includes(type)) return "high";
	if (mediumPriority.includes(type)) return "medium";
	return "low";
};

const getPosition = (type: string, index: number): string => {
	const positionMap: Record<string, string> = {
		origin: "top-left",
		philosophy: "top-right",
		scale_and_capability: "middle-left",
		innovation: "middle-right",
		ethics: "bottom-left",
		closing: "bottom-right",
	};

	if (positionMap[type]) return positionMap[type];

	const positions = [
		"top-left",
		"top-right",
		"middle-left",
		"middle-right",
		"bottom-left",
		"bottom-right",
	];
	return positions[index % positions.length];
};

// Fixed position styles - stable, no transforms on base state
const positionStyles: Record<string, string> = {
	"top-left": "-top-20 left-30",
	"top-right": "-top-10 right-20",
	"middle-left": "top-55 left-35",
	"middle-right": "top-65 right-28",
	"bottom-left": "bottom-10 left-35",
	"bottom-right": "bottom-0 right-30",
};

// Offset adjustments for fine-tuning positions
const getPositionOffset = (position: string): React.CSSProperties => {
	switch (position) {
		case "top-left":
			return { transform: "translate(10%, 10%)" };
		case "top-right":
			return { transform: "translate(-10%, 10%)" };
		case "middle-left":
			return { transform: "translate(10%, -50%)" };
		case "middle-right":
			return { transform: "translate(-10%, -50%)" };
		case "bottom-left":
			return { transform: "translate(10%, -10%)" };
		case "bottom-right":
			return { transform: "translate(-10%, -10%)" };
		default:
			return { transform: "translate(0, 0)" };
	}
};

const getPriorityStyles = (priority: "high" | "medium" | "low") => {
	switch (priority) {
		case "high":
			return {
				width: "w-72 md:w-80 lg:w-96",
				padding: "p-5 md:p-6",
				titleSize: "text-base md:text-lg",
				shadow: "shadow-xl",
				borderWidth: "border",
				bgIntensity: "bg-white/80",
				iconSize: "w-8 h-8",
			};
		case "medium":
			return {
				width: "w-64 md:w-72 lg:w-80",
				padding: "p-4 md:p-5",
				titleSize: "text-sm md:text-base",
				shadow: "shadow-lg",
				borderWidth: "border",
				bgIntensity: "bg-white/70",
				iconSize: "w-6 h-6",
			};
		default:
			return {
				width: "w-56 md:w-64 lg:w-72",
				padding: "p-3 md:p-4",
				titleSize: "text-xs md:text-sm",
				shadow: "shadow-md",
				borderWidth: "border",
				bgIntensity: "bg-white/60",
				iconSize: "w-5 h-5",
			};
	}
};

const getSectionIcon = (type: string): string => {
	const icons: Record<string, string> = {
		origin: "🌱",
		philosophy: "⚙️",
		scale_and_capability: "🏭",
		innovation: "💡",
		ethics: "🌟",
		closing: "🤝",
	};
	return icons[type] || "📌";
};

const getSectionTitle = (section: SectionItem): string => {
	if (section.heading) return section.heading;

	const titles: Record<string, string> = {
		origin: "Our Origin",
		philosophy: "Our Philosophy",
		scale_and_capability: "Scale & Capability",
		innovation: "Innovation",
		ethics: "Ethics & Integrity",
		closing: "A Call to Partnership",
	};
	return titles[section.type] || section.type.replace(/_/g, " ");
};

const cardVariants: Variants = {
	hidden: (custom: { x: number; y: number }) => ({
		opacity: 0,
		x: custom.x,
		y: custom.y,
		scale: 0.9,
	}),
	visible: {
		opacity: 1,
		x: 0,
		y: 0,
		scale: 1,
		transition: {
			type: "spring",
			damping: 20,
			stiffness: 300,
		},
	},
};

const imageVariants: Variants = {
	hidden: { opacity: 0, scale: 0.85, rotateY: -10 },
	visible: {
		opacity: 1,
		scale: 1,
		rotateY: 0,
		transition: {
			duration: 0.8,
			ease: [0.25, 0.1, 0.25, 1],
		},
	},
};

const quoteVariants: Variants = {
	hidden: { opacity: 0, y: 30 },
	visible: {
		opacity: 1,
		y: 0,
		transition: { duration: 0.6, delay: 0.2 },
	},
};

const headerVariants: Variants = {
	hidden: { opacity: 0, y: -20 },
	visible: {
		opacity: 1,
		y: 0,
		transition: { duration: 0.5 },
	},
};

// ================= MAIN COMPONENT =================
export const FoundersMessageComponent: React.FC<FoundersMessageProps> = ({
	data,
	className = "",
}) => {
	const sectionRef = useRef<HTMLElement>(null);
	const imageRef = useRef<HTMLDivElement>(null);
	const [isMobile, setIsMobile] = useState(false);
	const isInView = useInView(sectionRef, { once: true, amount: 0.2 });
	const prefersReducedMotion = useReducedMotion();

	useEffect(() => {
		const checkMobile = () => {
			setIsMobile(window.innerWidth < 768);
		};
		checkMobile();
		window.addEventListener("resize", checkMobile);
		return () => window.removeEventListener("resize", checkMobile);
	}, []);

	useEffect(() => {
		if (prefersReducedMotion || isMobile || !sectionRef.current) return;

		const ctx = gsap.context(() => {
			gsap.to(imageRef.current, {
				y: -60,
				scale: 1.05,
				duration: 1,
				scrollTrigger: {
					trigger: sectionRef.current,
					start: "top bottom",
					end: "bottom top",
					scrub: 1.2,
				},
			});

			const cards = document.querySelectorAll(".message-card");
			cards.forEach((card, index) => {
				const direction = index % 2 === 0 ? -1 : 1;
				gsap.to(card, {
					y: direction * 15,
					duration: 1,
					scrollTrigger: {
						trigger: sectionRef.current,
						start: "top bottom",
						end: "bottom top",
						scrub: 0.8,
					},
				});
			});
		});

		return () => ctx.revert();
	}, [prefersReducedMotion, isMobile]);

	const getEntryOffset = (position: string): { x: number; y: number } => {
		switch (position) {
			case "top-left":
				return { x: -80, y: -80 };
			case "top-right":
				return { x: 80, y: -80 };
			case "middle-left":
				return { x: -100, y: 0 };
			case "middle-right":
				return { x: 100, y: 0 };
			case "bottom-left":
				return { x: -80, y: 80 };
			case "bottom-right":
				return { x: 80, y: 80 };
			default:
				return { x: 0, y: 50 };
		}
	};

	const preparedSections = data.messageSections.sections.map(
		(section, index) => ({
			...section,
			priority: getPriority(section.type),
			position: getPosition(section.type, index),
			displayTitle: getSectionTitle(section),
			icon: getSectionIcon(section.type),
		}),
	);

	const sortedSections = [...preparedSections].sort((a, b) => {
		const priorityOrder = { high: 0, medium: 1, low: 2 };
		return priorityOrder[a.priority] - priorityOrder[b.priority];
	});

	// Mobile layout
	if (isMobile) {
		return (
			<section
				ref={sectionRef}
				className={`relative bg-gradient-to-b from-gray-50 via-white to-gray-50 py-16 md:py-24 overflow-hidden ${className}`}>
				<div className="absolute inset-0 opacity-30">
					<div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-200/40 rounded-full blur-[120px]" />
					<div className="absolute bottom-0 right-1/4 w-80 h-80 bg-purple-200/40 rounded-full blur-[120px]" />
				</div>
				<div
					className="absolute inset-0 opacity-[0.03]"
					style={{
						backgroundImage:
							"radial-gradient(circle at 25% 50%, rgba(0,0,0,0.1) 1px, transparent 1px)",
						backgroundSize: "40px 40px",
					}}
				/>

				<div className="relative z-10 max-w-7xl mx-auto px-5">
					<motion.div
						variants={headerVariants}
						initial="hidden"
						animate={isInView ? "visible" : "hidden"}
						className="text-center mb-8">
						<div className="flex items-center justify-center gap-2 mb-3">
							{data.meta.map((item, idx) => (
								<span
									key={idx}
									className="text-xs md:text-sm text-[#f06d29] tracking-wider">
									{idx > 0 && <span className="mx-2 text-gray-400">•</span>}
									{item}
								</span>
							))}
						</div>
						<h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-3">
							{data.title}
						</h2>
						<p className="text-gray-600 text-lg">{data.description}</p>
					</motion.div>

					{data.messageSections.heroQuote && (
						<motion.div
							variants={quoteVariants}
							initial="hidden"
							animate={isInView ? "visible" : "hidden"}
							className="text-center mb-12">
							<div className="inline-block px-4 py-1 rounded-full bg-blue-100 border border-blue-200 text-[#f06d29] text-xs font-medium tracking-wide mb-4">
								{data.labels?.leadershipInsight ?? "LEADERSHIP INSIGHT"}
							</div>
							<p className="text-xl md:text-2xl font-light italic text-gray-700 max-w-3xl mx-auto leading-relaxed">
								"{data.messageSections.heroQuote}"
							</p>
						</motion.div>
					)}

					<motion.div
						ref={imageRef}
						variants={imageVariants}
						initial="hidden"
						animate={isInView ? "visible" : "hidden"}
						className="flex justify-center mb-12">
						<div className="relative">
							<div className="absolute -inset-4 bg-gradient-to-tr from-blue-200/40 to-purple-200/40 rounded-2xl blur-2xl" />
							<div className="absolute -inset-1 rounded-2xl border border-gray-200" />
							<img
								src={data.image}
								alt={data.title}
								className="relative w-56 h-56 md:w-72 md:h-72 rounded-2xl object-cover shadow-2xl border-2 border-white"
							/>
							<div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur-sm px-4 py-1.5 rounded-full border border-gray-200 whitespace-nowrap shadow-md">
								<span className="text-xs font-medium text-[#f06d29]">
									{data.labels?.deskLabel ?? `${data.signatory.role}'s Desk`}
								</span>
							</div>
						</div>
					</motion.div>

					<div className="space-y-4 max-w-lg mx-auto">
						{sortedSections.map((section, idx) => {
							const styles = getPriorityStyles(section.priority);
							return (
								<motion.div
									key={section.type}
									initial={{ opacity: 0, y: 30 }}
									animate={isInView ? { opacity: 1, y: 0 } : {}}
									transition={{ delay: idx * 0.08, duration: 0.5 }}
									whileHover={{
										y: -8,
										scale: 1.02,
										boxShadow: "0 20px 40px -12px rgba(0, 0, 0, 0.15)",
									}}
									className={`message-card ${styles.width} ${styles.padding} ${styles.bgIntensity} backdrop-blur-md rounded-xl ${styles.borderWidth} border-gray-200 shadow-lg transition-all duration-300 cursor-pointer mx-auto hover:shadow-2xl hover:border-gray-300`}>
									<div className="flex items-start gap-3">
										<span className={`${styles.iconSize} text-2xl`}>
											{section.icon}
										</span>
										<div className="flex-1">
											<h4
												className={`font-semibold ${styles.titleSize} text-gray-900 mb-2`}>
												{section.displayTitle}
											</h4>
											<p className="text-gray-600 text-xs md:text-sm leading-relaxed">
												{section.content.length > 200 ?
													`${section.content.substring(0, 200)}...`
												:	section.content}
											</p>
										</div>
									</div>
								</motion.div>
							);
						})}
					</div>

					<motion.div
						initial={{ opacity: 0 }}
						animate={isInView ? { opacity: 1 } : {}}
						transition={{ delay: 0.6 }}
						className="text-center mt-12 pt-6 border-t border-gray-200">
						<p className="text-lg font-semibold text-gray-900">
							{data.signatory.name}
						</p>
						<p className="text-sm text-gray-500">{data.signatory.role}</p>
					</motion.div>
				</div>
			</section>
		);
	}

	// Desktop Layout with fixed positions
	return (
		<section
			ref={sectionRef}
			className={`relative bg-gradient-to-b from-gray-50 via-white to-gray-50 py-20 md:py-28 overflow-hidden ${className}`}>
			{/* Animated Background Gradients */}
			<div className="absolute inset-0 opacity-30">
				<div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-200/40 rounded-full blur-[120px] animate-pulse" />
				<div className="absolute bottom-0 right-1/4 w-80 h-80 bg-purple-200/40 rounded-full blur-[120px] animate-pulse delay-1000" />
				<div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-100/30 rounded-full blur-[100px]" />
			</div>

			<div className="absolute inset-0 opacity-[0.03]">
				<div
					className="absolute inset-0"
					style={{
						backgroundImage:
							"radial-gradient(circle at 25% 50%, rgba(0,0,0,0.1) 1px, transparent 1px)",
						backgroundSize: "50px 50px",
					}}
				/>
			</div>

			<div className="relative z-10 max-w-7xl mx-auto px-6">
				{/* Quote Section */}
				{data.messageSections.heroQuote && (
					<motion.div
						variants={quoteVariants}
						initial="hidden"
						animate={isInView ? "visible" : "hidden"}
						className="text-center mb-20">
						<div className="inline-block px-4 py-1 rounded-full bg-blue-100 border border-blue-200 text-[#f06d29] text-xs font-medium tracking-wide mb-4">
							{data.labels?.foundersVision ?? "FOUNDER'S VISION"}
						</div>
						<p className="text-xl md:text-2xl lg:text-3xl font-light italic text-gray-700 max-w-4xl mx-auto leading-relaxed">
							"{data.messageSections.heroQuote}"
						</p>
					</motion.div>
				)}

				{/* Main Visual Area - Fixed position container */}
				<div className="relative flex justify-center items-center min-h-[650px] md:min-h-[750px]">
					{/* Central Image */}
					<motion.div
						ref={imageRef}
						variants={imageVariants}
						initial="hidden"
						animate={isInView ? "visible" : "hidden"}
						className="relative z-10">
						<div className="absolute -inset-6 rounded-full bg-gradient-to-r from-blue-200/30 to-purple-200/30 blur-xl" />
						<div className="absolute -inset-3 rounded-full border border-gray-200" />
						<div className="absolute -inset-0 rounded-full border border-blue-200" />

						<div className="relative">
							<img
								src={data.image}
								alt={data.title}
								className="relative w-72 h-72 md:w-80 md:h-80 lg:w-100 lg:h-160 rounded-full object-cover shadow-2xl ring-4 ring-white"
							/>
							<div className="absolute inset-0 rounded-full bg-gradient-to-t from-gray-100/40 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity duration-500" />

							<div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur-md px-5 py-2 rounded-full border border-gray-200 whitespace-nowrap shadow-lg">
								<span className="text-sm font-semibold text-[#f06d29]">
									{data.signatory.name}
								</span>
							</div>
						</div>
					</motion.div>

					{/* Fixed Position Message Cards - NO MOVEMENT, just lift on hover */}
					{sortedSections.map((section, idx) => {
						const styles = getPriorityStyles(section.priority);
						const offset = getEntryOffset(section.position);
						const positionOffset = getPositionOffset(section.position);

						return (
							<motion.div
								key={section.type}
								custom={offset}
								variants={cardVariants}
								initial="hidden"
								animate={isInView ? "visible" : "hidden"}
								transition={{ delay: idx * 0.1, type: "spring", damping: 18 }}
								className={`message-card fixed-position-card absolute ${positionStyles[section.position]} ${styles.width} ${styles.padding} ${styles.bgIntensity} backdrop-blur-md rounded-xl ${styles.borderWidth} border-gray-200 shadow-lg transition-all duration-300 cursor-pointer group z-20`}
								style={{
									...positionOffset,
									backdropFilter: "blur(12px)",
									WebkitBackdropFilter: "blur(12px)",
									transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
								}}
								whileHover={{
									y: -12,
									scale: 1.05,
									boxShadow:
										"0 25px 40px -12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(37, 99, 235, 0.2)",
									transition: { duration: 0.25, ease: "easeOut" },
								}}>
								{/* Subtle hover glow */}
								<div className="absolute inset-0 rounded-xl bg-gradient-to-r from-white/0 via-gray-100/50 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

								{/* Additional glow effect on hover */}
								<div className="absolute -inset-px rounded-xl bg-gradient-to-r from-blue-400/0 via-blue-400/0 to-blue-400/0 group-hover:from-blue-400/10 group-hover:via-blue-400/5 group-hover:to-blue-400/10 transition-all duration-500 pointer-events-none" />

								{/* Priority Indicator Bar */}
								<div className="mb-3">
									<div
										className={`h-1 w-12 rounded-full transition-all duration-300 group-hover:w-16 ${
											section.priority === "high" ?
												"bg-blue-500 group-hover:bg-[#f06d29]"
											: section.priority === "medium" ?
												"bg-blue-400/60 group-hover:bg-[#f06d29]"
											:	"bg-blue-300/50 group-hover:bg-[#f06d29]"
										}`}
									/>
								</div>

								{/* Header with Icon and Title */}
								<div className="flex items-start gap-3 mb-3">
									<span
										className={`${styles.iconSize} text-2xl transition-all duration-300 group-hover:scale-110 group-hover:rotate-6`}>
										{section.icon}
									</span>
									<h4
										className={`font-bold ${styles.titleSize} text-gray-900 transition-all duration-300 group-hover:text-[#f06d29] group-hover:translate-x-1 leading-tight`}>
										{section.displayTitle}
									</h4>
								</div>

								{/* Description */}
								<p className="text-gray-600 text-xs md:text-sm leading-relaxed line-clamp-4 transition-all duration-300 group-hover:text-gray-800">
									{section.content}
								</p>
							</motion.div>
						);
					})}
				</div>
			</div>

			{/* Custom CSS for fixed positioning stability */}
			<style>{`
				.fixed-position-card {
					will-change: transform;
					transform-origin: center center;
					/* Ensure base position is stable */
					transform: translate(0, 0) !important;
				}
				
				.fixed-position-card:hover {
					z-index: 30;
				}
				
				/* Override any GSAP movement on hover */
				.fixed-position-card:active {
					transform: translate(0, 0) !important;
				}
				
				@media (min-width: 768px) {
					.fixed-position-card {
						position: absolute;
					}
				}
			`}</style>
		</section>
	);
};

export default FoundersMessageComponent;
